import {
    Avatar,
    Box,
    Chip,
    Dialog,
    DialogContent,
    IconButton,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Tooltip,
    Typography,
    useMediaQuery,
} from "@mui/material";

import { lighten } from "@mui/material/styles";
import type { ICatalogResult } from "../types/catalog";
import { useSelector } from "react-redux";
import type { RootState } from "../store";
import Theme from "../utils/theme";
import ChipsRow from "./ChipsRow";
import { useCallback, useMemo, useState } from "react";
import CatalogListDialog from "./CatalogListDialog";
import type { ISavedRecipes } from "../types/recipe";
import type { IPost } from "../types/post";
import axios from "axios";
import { Collections, OpenInNew, RestaurantMenu, Article, Person, Close } from "@mui/icons-material";
import { scrollbarCss } from "../utils/themeScrollbar";

function formatDate(iso: string) {
    try {
        return new Date(iso).toLocaleDateString(undefined, {
            year: "numeric",
            month: "short",
            day: "numeric",
        });
    } catch {
        return iso;
    }
}

export function CatalogListItem({ item }: { item: ICatalogResult }) {
    const themeState = useSelector((state: RootState) => state.theme);
    const theme = useMemo(() => Theme(themeState.mode), [themeState.mode]);
    const softPrimary = lighten(theme.palette.primary.main, 0.3);

    const [openDialog, setOpenDialog] = useState(false);
    const [dialogData, setDialogData] = useState<IPost | ISavedRecipes | null>(null);

    const isXs = useMediaQuery(theme.breakpoints.down("sm"));

    const onDialogOpen = useCallback(() => {
        if (item.docType === "post") {
            axios.get(`${process.env.REACT_APP_API_URL}/posts/fetchById`, { params: { postId: item.id } }).then((res) => {
                setDialogData(res.data.body);
                setOpenDialog(true);
            });
        } else if (item.docType === "recipe") {
            axios.get(`${process.env.REACT_APP_API_URL}/recipes/fetchById`, { params: { routeId: item.id } }).then((res) => {
                setDialogData(res.data.body);
                setOpenDialog(true);
            });
        } else return;
    }, [item]);

    const avatar = {
        recipe: { icon: <RestaurantMenu />, bg: theme.palette.success.main, src: "" },
        post: { icon: <Article />, bg: theme.palette.info.main, src: "" },
        user: { icon: <Person />, bg: "", src: item.content ?? "" },
    }[item.docType];

    const RecipeBody = useMemo(
        () => (
            <Box sx={{ maxWidth: "calc(100% - 50px)" }}>
                {/* Title row */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                    <Typography component="span" variant="subtitle1" fontWeight={600}>
                        {item.name}
                    </Typography>
                    {item.user && (
                        <Chip
                            component="a"
                            href={`/profile/${item.user}`}
                            size="small"
                            label={`${item.user}`}
                            variant="outlined"
                            onClick={() => {}}
                        />
                    )}
                    <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                        • {formatDate(item.docCreatedAt)}
                    </Typography>
                </Box>

                {/* Description */}
                {item.content && (
                    <Typography
                        component="span"
                        variant="body2"
                        color="text.secondary"
                        sx={{
                            mt: 0.5,
                            display: "-webkit-box",
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                        }}
                    >
                        {item.content}
                    </Typography>
                )}

                <ChipsRow items={item.tags} colorBg={softPrimary} />
                <ChipsRow items={item.arrayContent} outlined />
            </Box>
        ),
        [item, softPrimary]
    );

    const PostBody = useMemo(
        () => (
            <Box sx={{ maxWidth: "calc(100% - 50px)" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                    <Avatar sx={{ width: "20px", height: "20px" }} src={item.content} />
                    <Typography component="a" href={`/profile/${item.user}`} variant="subtitle1" fontWeight={600}>
                        {item.user}
                    </Typography>
                    <Typography component="span" variant="caption" color="text.secondary">
                        • {formatDate(item.docCreatedAt)}
                    </Typography>
                </Box>

                {item.content && (
                    <Typography
                        component="span"
                        variant="body2"
                        color="text.secondary"
                        sx={{
                            mt: 0.5,
                            display: "-webkit-box",
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                        }}
                    >
                        {item.name}
                    </Typography>
                )}

                <ChipsRow items={item.tags} colorBg={softPrimary} />
            </Box>
        ),
        [item, softPrimary]
    );

    const UserBody = useMemo(
        () => (
            <Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", height: "40px" }}>
                    <Typography component="a" href={`/profile/${item.user}`} variant="h6" sx={{ cursor: "pointer" }}>
                        {item.user}
                    </Typography>
                </Box>
            </Box>
        ),
        [item]
    );

    const Body = item.docType === "recipe" ? RecipeBody : item.docType === "post" ? PostBody : UserBody;

    return (
        <Box>
            <ListItem
                key={item.id}
                divider
                alignItems="flex-start"
                secondaryAction={
                    <Box>
                        {item.docType !== "user" && (
                            <Tooltip title="Open" sx={{ mr: 1 }}>
                                <IconButton edge="end" onClick={() => onDialogOpen()}>
                                    <Collections />
                                </IconButton>
                            </Tooltip>
                        )}
                        <Tooltip title="Open In Tab">
                            <IconButton
                                component="a"
                                href={
                                    item.docType === "post"
                                        ? `/post/${item.id}`
                                        : item.docType === "recipe"
                                        ? `/recipe/${item.id}`
                                        : `/profile/${item.user}`
                                }
                                rel="noopener noreferrer"
                            >
                                <OpenInNew />
                            </IconButton>
                        </Tooltip>
                    </Box>
                }
                sx={{ alignItems: "flex-start", pb: "12px" }}
            >
                <ListItemAvatar sx={{ my: "4px" }}>
                    <Avatar src={avatar.src} sx={{ bgcolor: avatar.bg }}>
                        {avatar.icon}
                    </Avatar>
                </ListItemAvatar>

                <ListItemText secondaryTypographyProps={{ component: "div" }} primary={null} secondary={Body} />
            </ListItem>

            {dialogData !== null && (
                <Dialog
                    open={openDialog}
                    onClose={() => setOpenDialog(false)}
                    maxWidth="lg"
                    fullWidth={!isXs}
                    fullScreen={isXs}
                    PaperProps={{
                        sx: {
                            width: "100vw",
                            height: "auto",
                            maxHeight: "100dvh",
                            m: 0,
                            borderRadius: 5,
                        },
                    }}
                >
                    <DialogContent sx={{ ...scrollbarCss, p: 0 }}>
                        <Box
                            sx={{
                                height: { xs: "100dvh", sm: "auto" },
                                position: "relative",
                                overflow: { xs: "auto", sm: "none" },
                                ...scrollbarCss,
                            }}
                        >
                            {item.docType !== "user" && (
                                <>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            justifyContent: "flex-end",
                                            position: item.docType === "recipe" ? "absolute" : "relative",
                                            right: 0,
                                            top: 0,
                                            zIndex: 999,
                                        }}
                                    >
                                        <IconButton
                                            onClick={() => {
                                                setOpenDialog(false);
                                            }}
                                        >
                                            <Close />
                                        </IconButton>
                                    </Box>
                                    <CatalogListDialog item={dialogData} dialogType={item.docType} setItem={setDialogData} />
                                </>
                            )}
                        </Box>
                    </DialogContent>
                </Dialog>
            )}
        </Box>
    );
}
